import type {
  EchoflicksBuildingProfile,
  EchoflicksCountCheck,
  EchoflicksFloorPlan,
  EchoflicksListingIngest,
  EchoflicksMediaRoom,
  EchoflicksPropertyType,
  EchoflicksSessionEvent,
  RoomMapEventPayload,
} from "../types";
import type { MediaUrlSource } from "./useMediaUrl";

type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const presentString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

export const sessionStatusLabel = (status?: string | null) => {
  if (status === "completed") return "Completed";
  if (status === "scripted") return "Scripted";
  if (status === "media_received") return "Media received";
  if (status === "failed") return "Failed";
  return "Unknown";
};

export const confidenceLabel = (confidence?: string | null) => {
  if (confidence === "exact") return "Exact";
  if (confidence === "inferred") return "Inferred";
  if (confidence === "user_level") return "User level";
  return "Unknown";
};

export const statusBadgeVariant = (status?: string | null): BadgeVariant => {
  if (status === "completed") return "default";
  if (status === "failed") return "destructive";
  if (status === "scripted" || status === "media_received") return "secondary";
  return "outline";
};

export const formatBytes = (bytes?: number | null) => {
  if (!bytes) return "Unknown size";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

/** Video position as m:ss.t — the format used by the echo feedback flow. */
export const formatVideoTimestamp = (seconds: number) => {
  const whole = Math.floor(seconds);
  const tenths = Math.floor((seconds - whole) * 10);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}.${tenths}`;
};

export const humanizeMediaKind = (kind?: string | null) => {
  if (kind === "uploaded_media") return "Uploaded media";
  if (kind === "generated_asset") return "Generated asset";
  if (kind === "i2v") return "I2V scene";
  if (kind === "transition_whip") return "Transition whip";
  if (kind === "title_card") return "Title card";
  if (kind === "map_clip") return "Map";
  if (kind === "drone_shot") return "Drone / Aerial Shot";
  if (kind === "final_video") return "Final video";
  if (kind === "script_json") return "Script JSON";
  if (kind === "ingest_extraction") return "Listing ingest";
  return "Unknown media";
};

// Per-scene generated-asset roles (composer `scene.asset.created` -> dashboard MEDIA
// rows). Real-estate echoes get the map clip and the aerial "dollhouse" drone shot as
// their own roles so the CRM can show them as separate, labelled sections.
export const ASSET_ROLE_META: Record<string, { label: string }> = {
  map_clip: { label: "Map" },
  drone_shot: { label: "Drone / Aerial Shot" },
  i2v: { label: "I2V scene" },
  transition_whip: { label: "Transition whip" },
  title_card: { label: "Title card" },
};

export const humanizeAssetRole = (role?: string | null) =>
  (role ? ASSET_ROLE_META[role]?.label : null) ?? null;

export const isRealEstateMedia = (media: { asset_role?: string | null }) =>
  media.asset_role === "map_clip" || media.asset_role === "drone_shot";

// A session is real-estate if the indexer stamped echo_type, or — GCP-robust fallback —
// any of its generated media is a map clip or drone shot.
export const isRealEstateSession = (
  session: { echo_type?: string | null } | null | undefined,
  media: ReadonlyArray<{ asset_role?: string | null }> = [],
) => session?.echo_type === "real_estate" || media.some(isRealEstateMedia);

export const isImage = (mimeType?: string | null) =>
  Boolean(mimeType?.startsWith("image/"));

export const isVideo = (mimeType?: string | null) =>
  Boolean(mimeType?.startsWith("video/"));

export const isAudio = (mimeType?: string | null) =>
  Boolean(mimeType?.startsWith("audio/"));

// HEIC/HEIF (iPhone photos) are images, but browsers can't decode them in an
// <img>, so they must be converted client-side before display. Detect them by
// mime type OR file extension, because uploaded_media rows often carry no mime.
export const isHeic = (mimeType?: string | null, path?: string | null) => {
  const mime = mimeType?.toLowerCase() ?? "";
  if (mime === "image/heic" || mime === "image/heif") return true;
  const lower = path?.toLowerCase() ?? "";
  return lower.endsWith(".heic") || lower.endsWith(".heif");
};

// --- Listing-ingest observability (kind === "ingest_extraction" rows) -------
// The bot publishes one ExtractionRecord per ingest attempt; a session can
// hold several (retries, recipe-primary + LLM shadow). These helpers pick the
// primary record and diff field maps for the recipe-vs-LLM comparison.

type IngestMediaRow = {
  kind: string;
  created_at_source?: string | null;
  ingest?: EchoflicksListingIngest | null;
};

export const ingestRecordsOf = <T extends IngestMediaRow>(
  media: ReadonlyArray<T>,
): T[] =>
  media
    .filter((row) => row.kind === "ingest_extraction" && row.ingest)
    .sort((a, b) =>
      String(b.created_at_source ?? "").localeCompare(
        String(a.created_at_source ?? ""),
      ),
    );

export const primaryIngestRecord = <T extends IngestMediaRow>(
  records: ReadonlyArray<T>,
): T | null =>
  records.find((row) => row.ingest?.outcome === "success") ??
  records[0] ??
  null;

// Field order + labels for the ingest field table and the comparison view.
export const INGEST_FIELD_META: Array<{ key: string; label: string }> = [
  { key: "title", label: "Title" },
  { key: "price_display", label: "Price" },
  { key: "bedrooms", label: "Bedrooms" },
  { key: "bathrooms", label: "Bathrooms" },
  { key: "area", label: "Area" },
  { key: "location_text", label: "Location" },
  { key: "amenities", label: "Amenities" },
  // Listing signals (LVS, bot record `signals_*` flat twins): what the page itself
  // featured and how its copy is written. Absent on pre-signals records.
  { key: "signals_highlights", label: "Highlights (page)" },
  { key: "signals_hero", label: "Hero photo" },
  { key: "signals_featured", label: "Featured headlines" },
  { key: "signals_voice", label: "Listing voice" },
  { key: "description_chars", label: "Description (chars)" },
  { key: "image_count", label: "Gallery images" },
];

export const ingestFieldDisplay = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return String(value);
};

const normalizedIngestValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value))
    return value.map(String).sort().join("|").toLowerCase();
  return String(value).replace(/\s+/g, " ").trim().toLowerCase();
};

// Field keys where two extractions meaningfully disagree (description length
// gets a 10% tolerance — re-encodes/whitespace must not read as a mismatch).
export const diffIngestFieldKeys = (
  a?: Record<string, unknown> | null,
  b?: Record<string, unknown> | null,
): string[] => {
  if (!a || !b) return [];
  const diffs: string[] = [];
  for (const { key } of INGEST_FIELD_META) {
    const va = a[key];
    const vb = b[key];
    if (key === "description_chars") {
      const na = Number(va) || 0;
      const nb = Number(vb) || 0;
      if (Math.abs(na - nb) > Math.max(50, 0.1 * Math.max(na, nb)))
        diffs.push(key);
      continue;
    }
    if (normalizedIngestValue(va) !== normalizedIngestValue(vb))
      diffs.push(key);
  }
  return diffs;
};

export const ingestOutcomeBadgeVariant = (
  outcome?: string | null,
): BadgeVariant => {
  if (outcome === "success") return "default";
  if (outcome === "blocked" || outcome === "fetch_error") return "destructive";
  return "secondary";
};

export const formatMs = (ms?: number | null) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

// --- Floor plan (FPR-016) ----------------------------------------------------
// `ingest.floor_plan` (floor_plan.v1) is additive: absent on ingests that
// predate the lane or found no plan. Every helper feature-detects its input.

export const floorPlanStatusTone = (
  status?: string | null,
): { variant: BadgeVariant; className: string; label: string } => {
  if (status === "read")
    return {
      variant: "outline",
      className: "border-emerald-500 text-emerald-600",
      label: "read",
    };
  if (status === "unusable")
    return {
      variant: "outline",
      className: "border-amber-500 text-amber-600",
      label: "unusable",
    };
  if (status === "failed")
    return { variant: "destructive", className: "", label: "failed" };
  return {
    variant: "outline",
    className: "text-muted-foreground",
    label: status ?? "absent",
  };
};

export const formatConfidence = (value?: number | null): string =>
  isNumber(value) ? value.toFixed(2) : "—";

export const formatAreaM2 = (value?: number | null): string => {
  if (!isNumber(value)) return "—";
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)} m²`;
};

export const humanizeFloorPlanSourceKind = (kind?: string | null): string => {
  if (kind === "pdf_vector") return "vector PDF";
  if (kind === "pdf_raster") return "raster PDF";
  if (kind === "image") return "image";
  return kind ?? "unknown source";
};

/** One-line digest: source kind · pages (+text layer) · rooms · floors · confidence · model. */
export const floorPlanSummaryLine = (
  plan?: EchoflicksFloorPlan | null,
): string => {
  if (!plan) return "";
  const parts: string[] = [];
  if (plan.source_kind)
    parts.push(humanizeFloorPlanSourceKind(plan.source_kind));
  const pages = plan.pages ?? [];
  const pageCount = plan.page_count_total ?? pages.length;
  if (pageCount > 0) {
    const textLayer = pages.some((page) => page.text_layer === true);
    parts.push(
      `${pageCount} page${pageCount === 1 ? "" : "s"}${textLayer ? " · text layer" : ""}`,
    );
  }
  const rooms = plan.rooms?.length ?? 0;
  if (rooms > 0) parts.push(`${rooms} room${rooms === 1 ? "" : "s"}`);
  const floors = plan.floors?.length ?? 0;
  if (floors > 1) parts.push(`${floors} floors`);
  // floor_plan.v2 only — a v1 block has no `openings` and the line is unchanged.
  const openings = Array.isArray(plan.openings) ? plan.openings.length : 0;
  if (openings > 0)
    parts.push(`${openings} opening${openings === 1 ? "" : "s"}`);
  if (isNumber(plan.confidence))
    parts.push(`confidence ${formatConfidence(plan.confidence)}`);
  if (plan.model) parts.push(plan.model);
  return parts.join(" · ");
};

export type CountCheckEntry = { key: string; label: string; mismatch: boolean };

const COUNT_CHECK_ORDER = ["bedrooms", "bathrooms"];

const titleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ");

/**
 * "Bedrooms: mapped 4 · plan 5 · listing 6" — only the figures that exist.
 * A BLD room map adds `inferred` (placeholder rooms on a floor the plan does
 * not show): "mapped 2 · plan 0 · inferred 2 · listing 2".
 */
export const countCheckLabel = (
  kind: string,
  entry?: EchoflicksCountCheck[string] | null,
): string => {
  const parts: string[] = [];
  const mapped = entry?.mapped;
  const plan = entry?.plan;
  const inferred = entry?.inferred;
  const listing = entry?.listing;
  if (isNumber(mapped)) parts.push(`mapped ${mapped}`);
  if (isNumber(plan)) parts.push(`plan ${plan}`);
  if (isNumber(inferred)) parts.push(`inferred ${inferred}`);
  if (isNumber(listing)) parts.push(`listing ${listing}`);
  return `${titleCase(kind)}: ${parts.length ? parts.join(" · ") : "—"}`;
};

/**
 * The bot's own `ok` verdict wins. Without it (the room-map event omits it)
 * derive: plan ≠ listing is a mismatch; more rooms MAPPED than the plan (or,
 * with no plan figure, the listing) has is a mismatch. Fewer mapped than exist
 * is normal — not every room gets photographed. Inferred rooms (BLD) count
 * with the plan's: they stand in for the floor the plan does not show.
 */
export const countCheckMismatch = (
  entry?: EchoflicksCountCheck[string] | null,
): boolean => {
  if (!entry) return false;
  if (typeof entry.ok === "boolean") return !entry.ok;
  const { listing, mapped } = entry;
  const plan =
    isNumber(entry.plan) && isNumber(entry.inferred)
      ? entry.plan + entry.inferred
      : entry.plan;
  if (isNumber(plan) && isNumber(listing) && plan !== listing) return true;
  if (isNumber(mapped) && isNumber(plan)) return mapped > plan;
  if (isNumber(mapped) && isNumber(listing)) return mapped > listing;
  return false;
};

export const countCheckEntries = (
  check?: EchoflicksCountCheck | null,
): CountCheckEntry[] => {
  if (!isRecord(check)) return [];
  const rank = (key: string) => {
    const index = COUNT_CHECK_ORDER.indexOf(key);
    return index === -1 ? COUNT_CHECK_ORDER.length : index;
  };
  return Object.keys(check)
    .filter((key) => isRecord(check[key]))
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((key) => ({
      key,
      label: countCheckLabel(key, check[key]),
      mismatch: countCheckMismatch(check[key]),
    }));
};

export type FloorPlanRoomRow = {
  id: string;
  labelRaw: string;
  name: string;
  category: string;
  floor: string;
  /** `floor_plan.v2` level this room sits on; null on every v1 block. */
  levelId: string | null;
  area: string;
  areaGrounded: boolean;
  confidence: string;
  notes: string[];
};

const compareFloors = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true });

/** Rooms in plan order, grouped by floor when the plan spans several. */
export const roomsTableRows = (
  plan?: EchoflicksFloorPlan | null,
): FloorPlanRoomRow[] => {
  const rooms = plan && Array.isArray(plan.rooms) ? plan.rooms : [];
  const rows = rooms.map((room, index) => {
    const ensuiteOf = presentString(room.ensuite_of);
    return {
      id: presentString(room.room_id) ?? `room-${index + 1}`,
      labelRaw: presentString(room.label_raw) ?? "—",
      name: presentString(room.name) ?? "—",
      category: presentString(room.category) ?? "—",
      floor: presentString(room.floor) ?? "—",
      levelId: presentString(room.level_id),
      area: formatAreaM2(room.area_m2),
      areaGrounded: room.area_grounded === true && isNumber(room.area_m2),
      confidence: formatConfidence(room.confidence),
      notes: [
        room.service_room ? "service room" : null,
        ensuiteOf ? `ensuite of ${ensuiteOf}` : null,
        ...(Array.isArray(room.features) ? room.features : []),
      ].filter((note): note is string => Boolean(note)),
      order: index,
    };
  });
  const floors = new Set(rows.map((row) => row.floor));
  if (floors.size > 1) {
    rows.sort((a, b) => compareFloors(a.floor, b.floor) || a.order - b.order);
  }
  return rows.map(({ order: _order, ...row }) => row);
};

export type FloorPlanLevelGroup = {
  /** The level id, or "" for the rooms the reader left unassigned. */
  key: string;
  label: string;
  ordinal: number | null;
  /** BLD level model v2; null on every pre-BLD block. */
  kind: string | null;
  storey: number | null;
  /** The building's label, only when the plan set has more than one. */
  building: string | null;
  rows: FloorPlanRoomRow[];
};

/** Main building (or no building at all) first, then the others by id. */
const buildingRank = (
  buildingId: string | null,
  mainIds: ReadonlySet<string>,
): string => (!buildingId || mainIds.has(buildingId) ? "" : buildingId);

/**
 * `floor_plan.v2` rooms grouped by level, levels ordered by ordinal (0 = the
 * entrance level, −1 below it) then label. Empty for a v1 block, for a v2
 * block with a single level, and whenever no room carries a `level_id` — the
 * card then keeps its flat table exactly as before.
 */
export const floorPlanLevelGroups = (
  plan?: EchoflicksFloorPlan | null,
): FloorPlanLevelGroup[] => {
  const levels = plan && Array.isArray(plan.levels) ? plan.levels : [];
  if (levels.length === 0) return [];
  const rows = roomsTableRows(plan);
  if (!rows.some((row) => row.levelId)) return [];

  // BLD: levels may belong to several buildings (main house, garage, annex).
  const rawBuildings = plan?.buildings;
  const buildingLabels = new Map<string, string>();
  const mainIds = new Set<string>(["B0"]);
  for (const building of Array.isArray(rawBuildings) ? rawBuildings : []) {
    if (!isRecord(building)) continue;
    const id = presentString(building.building_id);
    if (!id) continue;
    buildingLabels.set(id, presentString(building.label) ?? id);
    if (building.kind === "main") mainIds.add(id);
  }

  const meta = new Map<
    string,
    {
      label: string;
      ordinal: number | null;
      sort: number | null;
      kind: string | null;
      storey: number | null;
      buildingId: string | null;
    }
  >();
  levels.forEach((level, index) => {
    const id = presentString(level.level_id) ?? `level-${index + 1}`;
    const ordinal = isNumber(level.ordinal) ? level.ordinal : null;
    meta.set(id, {
      label: presentString(level.label) ?? id,
      ordinal,
      sort: isNumber(level.ordinal_sort) ? level.ordinal_sort : ordinal,
      kind: presentString(level.kind),
      storey: isNumber(level.storey) ? level.storey : null,
      buildingId: presentString(level.building_id),
    });
  });
  const distinctBuildings = new Set(
    [...meta.values()].map((level) => level.buildingId ?? "B0"),
  );

  const groups = new Map<
    string,
    FloorPlanLevelGroup & { sort: number | null; rank: string }
  >();
  for (const row of rows) {
    const key = row.levelId ?? "";
    let group = groups.get(key);
    if (!group) {
      const known = key ? meta.get(key) : undefined;
      const buildingId = known?.buildingId ?? null;
      group = {
        key,
        label: known?.label ?? (key || "—"),
        ordinal: known?.ordinal ?? null,
        kind: known?.kind ?? null,
        storey: known?.storey ?? null,
        building:
          distinctBuildings.size > 1 && buildingId
            ? (buildingLabels.get(buildingId) ?? buildingId)
            : null,
        rows: [],
        sort: known?.sort ?? null,
        rank: buildingRank(buildingId, mainIds),
      };
      groups.set(key, group);
    }
    group.rows.push(row);
  }
  if (groups.size < 2 && !groups.has("")) {
    // A single-level v2 plan reads exactly like v1 — no header rows needed.
    return [];
  }

  // Unassigned rooms sort last; otherwise the main building first, then
  // ordinal (BLD `ordinal_sort` when present), then label.
  return [...groups.values()]
    .sort((a, b) => {
      if (!a.key !== !b.key) return a.key ? -1 : 1;
      if (a.rank !== b.rank) return a.rank.localeCompare(b.rank);
      if (a.sort !== null && b.sort !== null) return a.sort - b.sort;
      if (a.sort !== null) return -1;
      if (b.sort !== null) return 1;
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    })
    .map(({ sort: _sort, rank: _rank, ...group }) => group);
};

/**
 * Page renders as signable media sources — the same `useMediaUrl` →
 * POST /media/sign path the Submitted Media grid uses for `input/…` keys.
 * Without a bucket the hook stays idle and the card shows a placeholder.
 */
export const floorPlanPageSources = (
  plan: EchoflicksFloorPlan | null | undefined,
  bucket: string | null,
): Array<{ key: string; source: MediaUrlSource }> =>
  (plan && Array.isArray(plan.page_keys) ? plan.page_keys : [])
    .map((key) => presentString(key))
    .filter((key): key is string => key !== null)
    .map((key) => ({
      key,
      source: {
        url: null,
        storage_bucket: bucket,
        storage_path: key,
        storage_is_public: false,
      },
    }));

/** The bucket page renders live in: the ingest row's own, else a sibling upload's. */
export const ingestMediaBucket = (
  record: { storage_bucket?: string | null; bucket?: string | null },
  media: ReadonlyArray<{
    kind: string;
    storage_bucket?: string | null;
    bucket?: string | null;
  }> = [],
): string | null =>
  presentString(record.storage_bucket) ??
  presentString(record.bucket) ??
  media
    .filter((row) => row.kind === "uploaded_media")
    .map(
      (row) => presentString(row.storage_bucket) ?? presentString(row.bucket),
    )
    .find((bucket): bucket is string => bucket !== null) ??
  null;

// --- Property type (FPV-031) -------------------------------------------------
// `floor_plan.v2` blocks, the room-map event and the `property.type_resolved`
// dashboard event all carry the same shape; the dashboard indexer additionally
// projects it flat onto the ECHO row. Everything here is feature-detected: a
// legacy echo has none of it and every surface renders as it did before.

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: "Apartment",
  penthouse: "Penthouse",
  duplex_apartment: "Duplex apartment",
  studio: "Studio",
  loft: "Loft",
  house: "House",
  villa: "Villa",
  townhouse: "Townhouse",
  semi_detached: "Semi-detached",
  country_house: "Country house",
  plot: "Plot",
  commercial: "Commercial",
};

/**
 * "duplex_apartment" → "Duplex apartment". Null for the catch-all `other`,
 * for `unknown` and for anything absent — as agreed with the portal, a
 * property type we cannot name is shown as nothing rather than as "Other".
 * An enum member the CRM has not learnt yet is humanised, not dropped.
 */
export const propertyTypeLabel = (value?: string | null): string | null => {
  const key = presentString(value)?.toLowerCase();
  if (!key || key === "other" || key === "unknown") return null;
  return PROPERTY_TYPE_LABELS[key] ?? titleCase(key);
};

/** 0.86 → "86%"; "—" is never useful on a chip, so absent reads as null. */
export const formatPercent = (value?: number | null): string | null =>
  isNumber(value) ? `${Math.round(value * 100)}%` : null;

export type PropertyTypeSummary = {
  value: string;
  label: string;
  /** Humanised: "listing_text+plan" → "listing text + plan". */
  source: string | null;
  confidence: number | null;
  /** "86%", or null when the producer sent no confidence. */
  confidencePercent: string | null;
  evidence: string[];
};

const humanizePropertyTypeSource = (source: string): string =>
  source.replaceAll("_", " ").replaceAll("+", " + ");

/** Normalise any of the three carriers into one display shape. */
export const propertyTypeSummary = (
  raw?: EchoflicksPropertyType | null,
): PropertyTypeSummary | null => {
  if (!isRecord(raw)) return null;
  const value = presentString(raw.value);
  const label = propertyTypeLabel(value);
  if (!value || !label) return null;
  const source = presentString(raw.source);
  const confidence = isNumber(raw.confidence) ? raw.confidence : null;
  return {
    value: value.toLowerCase(),
    label,
    source: source ? humanizePropertyTypeSource(source) : null,
    confidence,
    confidencePercent: formatPercent(confidence),
    evidence: (Array.isArray(raw.evidence) ? raw.evidence : [])
      .map((item) => presentString(item))
      .filter((item): item is string => item !== null),
  };
};

/** The latest `property.type_resolved` event's payload, or null. */
export const propertyTypeFromEvents = (
  events: ReadonlyArray<EchoflicksSessionEvent>,
): PropertyTypeSummary | null => {
  const matching = (events ?? [])
    .filter((event) => event.event_type === "property.type_resolved")
    .sort((a, b) =>
      String(a.occurred_at ?? "").localeCompare(String(b.occurred_at ?? "")),
    );
  const latest = matching[matching.length - 1];
  if (!latest || !isRecord(latest.raw_payload)) return null;
  const raw = latest.raw_payload;
  const payload = isRecord(raw.payload) ? raw.payload : raw;
  return propertyTypeSummary(payload as EchoflicksPropertyType);
};

/**
 * The echo's property type: the indexer's flat row projection first (cheap,
 * always loaded), else the raw `property.type_resolved` event — the row
 * fields are absent on every echo indexed before FPV-032 shipped.
 */
export const propertyTypeFromRow = (
  row?: {
    property_type?: unknown;
    property_type_source?: unknown;
    property_type_confidence?: unknown;
  } | null,
  events: ReadonlyArray<EchoflicksSessionEvent> = [],
): PropertyTypeSummary | null => {
  const fromRow = isRecord(row)
    ? propertyTypeSummary({
        value: presentString(row.property_type),
        source: presentString(row.property_type_source),
        confidence: isNumber(row.property_type_confidence)
          ? row.property_type_confidence
          : null,
      })
    : null;
  if (!fromRow) return propertyTypeFromEvents(events);
  // The row carries no evidence list; borrow the event's when it agrees.
  const fromEvent = propertyTypeFromEvents(events);
  if (fromEvent && fromEvent.value === fromRow.value) {
    return {
      ...fromRow,
      source: fromRow.source ?? fromEvent.source,
      confidence: fromRow.confidence ?? fromEvent.confidence,
      confidencePercent:
        fromRow.confidencePercent ?? fromEvent.confidencePercent,
      evidence: fromEvent.evidence,
    };
  }
  return fromRow;
};

// --- Photo → room (FPR-033) --------------------------------------------------

/**
 * The level a mapped room sits on (`floor_plan.v2`, FPV-031). The printed
 * label when the projection carries one, else the raw level id — still enough
 * to tell two floors apart. Null on every v1 echo.
 */
export const roomLevelLabel = (
  room?: EchoflicksMediaRoom | null,
): string | null =>
  presentString(room?.level_label) ?? presentString(room?.level_id);

/** "room: Bedroom 3 (Planta 1) · 0.91"; null without a name. */
export const roomBadgeLabel = (
  room?: EchoflicksMediaRoom | null,
): string | null => {
  const name = presentString(room?.name);
  if (!name) return null;
  const level = roomLevelLabel(room);
  const named = level ? `${name} (${level})` : name;
  const confidence = formatConfidence(room?.confidence);
  return confidence === "—"
    ? `room: ${named}`
    : `room: ${named} · ${confidence}`;
};

export const roomBadgeTitle = (room?: EchoflicksMediaRoom | null): string => {
  const name = presentString(room?.name) ?? "unknown room";
  const parts = [
    `Room map (director skill): this photo was assigned to "${name}"`,
  ];
  const level = roomLevelLabel(room);
  if (level) parts.push(`level ${level}`);
  const category = presentString(room?.category);
  if (category) parts.push(`category ${category}`);
  const roomId = presentString(room?.room_id);
  if (roomId) parts.push(roomId);
  const confidence = room?.confidence;
  if (isNumber(confidence))
    parts.push(`confidence ${formatConfidence(confidence)}`);
  return parts.join(" · ");
};

/** The latest `director.skill_room_map` event's payload, or null. */
export const roomMapFromEvents = (
  events: ReadonlyArray<EchoflicksSessionEvent>,
): RoomMapEventPayload | null => {
  const matching = (events ?? [])
    .filter((event) => event.event_type === "director.skill_room_map")
    .sort((a, b) =>
      String(a.occurred_at ?? "").localeCompare(String(b.occurred_at ?? "")),
    );
  const latest = matching[matching.length - 1];
  if (!latest || !isRecord(latest.raw_payload)) return null;
  const raw = latest.raw_payload;
  const payload = isRecord(raw.payload) ? raw.payload : raw;
  return payload as RoomMapEventPayload;
};

export const humanizeRoomMapSource = (source?: string | null): string => {
  if (source === "floor_plan_llm") return "floor plan · LLM";
  if (source === "floor_plan_llm_fallback") return "floor plan · LLM fallback";
  if (source === "none") return "no room map";
  return source ?? "unknown";
};

export const roomMapSummaryLine = (
  payload?: RoomMapEventPayload | null,
): string => {
  if (!payload) return "";
  const parts: string[] = [];
  if (isNumber(payload.photos)) parts.push(`${payload.photos} photos`);
  if (isNumber(payload.assigned)) parts.push(`${payload.assigned} assigned`);
  if (isNumber(payload.unassigned))
    parts.push(`${payload.unassigned} unassigned`);
  if (isNumber(payload.exterior)) parts.push(`${payload.exterior} exterior`);
  if (isNumber(payload.rooms_with_photos) || isNumber(payload.rooms_total)) {
    parts.push(
      `rooms with photos ${payload.rooms_with_photos ?? "—"} / ${payload.rooms_total ?? "—"}`,
    );
  }
  return parts.join(" · ");
};

export type RoomMapLevel = {
  levelId: string;
  label: string;
  ordinal: number | null;
  photos: number | null;
};

export type RoomMapLevelSummary = RoomMapLevel & {
  /** Plan rooms (plus inferred placeholders) the map holds on this level. */
  rooms: number | null;
  /** BLD level model v2; null on every pre-BLD payload. */
  kind: string | null;
  storey: number | null;
  buildingId: string | null;
  /** A level the plan does not show (`source: "inferred"`, BLD). */
  inferred: boolean;
};

/**
 * The plan's levels as the room map saw them (`floor_plan.v2`, FPV-021, plus
 * the BLD kind/storey/building/source), main building first, then ordinal,
 * then label. Empty on every v1 payload.
 */
export const roomMapLevelSummaries = (
  payload?: RoomMapEventPayload | null,
): RoomMapLevelSummary[] => {
  const levels = payload && Array.isArray(payload.levels) ? payload.levels : [];
  const withPhotos: Record<string, unknown> =
    payload && isRecord(payload.levels_with_photos)
      ? payload.levels_with_photos
      : {};
  const mainIds = new Set<string>(["B0"]);
  return levels
    .filter((level): level is NonNullable<typeof level> => isRecord(level))
    .map((level, index) => {
      const levelId = presentString(level.level_id) ?? `level-${index + 1}`;
      const tally = withPhotos[levelId];
      return {
        levelId,
        label: presentString(level.label) ?? levelId,
        ordinal: isNumber(level.ordinal) ? level.ordinal : null,
        photos: isNumber(level.photos)
          ? level.photos
          : isNumber(tally)
            ? tally
            : null,
        rooms: isNumber(level.rooms) ? level.rooms : null,
        kind: presentString(level.kind),
        storey: isNumber(level.storey) ? level.storey : null,
        buildingId: presentString(level.building_id),
        inferred: level.source === "inferred",
      };
    })
    .sort((a, b) => {
      const rankA = buildingRank(a.buildingId, mainIds);
      const rankB = buildingRank(b.buildingId, mainIds);
      if (rankA !== rankB) return rankA.localeCompare(rankB);
      if (a.ordinal !== null && b.ordinal !== null)
        return a.ordinal - b.ordinal;
      if (a.ordinal !== null) return -1;
      if (b.ordinal !== null) return 1;
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    });
};

/**
 * The plan's levels as the room map saw them (`floor_plan.v2`, FPV-021),
 * ordered by ordinal then label. Empty on every v1 payload.
 */
export const levelsFromRoomMap = (
  payload?: RoomMapEventPayload | null,
): RoomMapLevel[] =>
  roomMapLevelSummaries(payload).map(({ levelId, label, ordinal, photos }) => ({
    levelId,
    label,
    ordinal,
    photos,
  }));

/**
 * Inferred placeholder rooms (BLD contract §4) have closed-set ids such as
 * `x-I1-bedroom-1`; plan rooms never start with `x-`.
 */
export const isInferredRoomId = (roomId?: string | null): boolean =>
  presentString(roomId)?.startsWith("x-") ?? false;

/**
 * Up to eight furnishing nouns the orchestrator read off a room's photos
 * (FPV-022), in payload order. Empty for a v1 payload, an unknown room, or a
 * room whose photos named nothing the lexicon recognises.
 */
export const furnishingsFor = (
  payload: RoomMapEventPayload | null | undefined,
  roomId: string | null | undefined,
): string[] => {
  const id = presentString(roomId);
  const furnishings: Record<string, unknown> =
    payload && isRecord(payload.room_furnishings)
      ? payload.room_furnishings
      : {};
  if (!id) return [];
  const entry = furnishings[id];
  if (!Array.isArray(entry)) return [];
  const seen = new Set<string>();
  return entry
    .map((item) => presentString(item))
    .filter((item): item is string => {
      if (item === null || seen.has(item.toLowerCase())) return false;
      seen.add(item.toLowerCase());
      return true;
    })
    .slice(0, 8);
};

export type RoomMapPhotoRow = {
  file: string;
  room: string;
  roomId: string | null;
  /** Level label (`floor_plan.v2`); null when the payload carries no levels. */
  level: string | null;
  confidence: string;
  evidence: string;
  /** Placed on a room or level the plan does not show (BLD inferred). */
  inferred: boolean;
};

/** Per-photo assignments, grouped by room name then natural file order. */
export const roomMapPhotoRows = (
  payload?: RoomMapEventPayload | null,
): RoomMapPhotoRow[] => {
  const photoRooms: Record<string, unknown> =
    payload && isRecord(payload.photo_rooms) ? payload.photo_rooms : {};
  const summaries = roomMapLevelSummaries(payload);
  const levels = new Map(
    summaries.map((level) => [level.levelId, level.label]),
  );
  const inferredLevels = new Set(
    summaries.filter((level) => level.inferred).map((level) => level.levelId),
  );
  return Object.entries(photoRooms)
    .filter((entry): entry is [string, Record<string, unknown>] =>
      isRecord(entry[1]),
    )
    .map(([file, value]) => {
      const levelId = presentString(value.level_id);
      const roomId = presentString(value.room_id);
      return {
        file,
        room: presentString(value.name) ?? "—",
        roomId,
        level: levelId ? (levels.get(levelId) ?? levelId) : null,
        confidence: formatConfidence(
          isNumber(value.confidence) ? value.confidence : null,
        ),
        evidence: presentString(value.evidence) ?? "",
        inferred:
          isInferredRoomId(roomId) ||
          (levelId !== null && inferredLevels.has(levelId)),
      };
    })
    .sort(
      (a, b) =>
        a.room.localeCompare(b.room) ||
        a.file.localeCompare(b.file, undefined, { numeric: true }),
    );
};

export type RoomMapDiagnosticChip = {
  key: string;
  label: string;
  count: number;
  warn: boolean;
};

// Counters that describe work done, not problems — never amber.
const ROOM_MAP_INFO_DIAGNOSTICS = new Set(["closure_votes"]);

export const roomMapDiagnosticChips = (
  diagnostics?: Record<string, unknown> | null,
): RoomMapDiagnosticChip[] => {
  const source: Record<string, unknown> = isRecord(diagnostics)
    ? diagnostics
    : {};
  return Object.entries(source)
    .filter((entry): entry is [string, number] => isNumber(entry[1]))
    .map(([key, count]) => ({
      key,
      label: `${key.replaceAll("_", " ")} ${count}`,
      count,
      warn: count > 0 && !ROOM_MAP_INFO_DIAGNOSTICS.has(key),
    }));
};

// --- Building profile, plan coverage and levels (BLD-043) --------------------
// Everything below is additive and feature-detected: a payload without the BLD
// fields yields empty lists / null and every card renders exactly as before.
// Copy rule: no em dashes in anything shown to an operator.

/** 1.5 → "1½", 2 → "2", 0.5 → "½"; null for anything that is not a number ≥ 0. */
export const formatStoreys = (value?: number | null): string | null => {
  if (!isNumber(value) || value < 0) return null;
  const whole = Math.floor(value);
  const fraction = value - whole;
  if (fraction === 0) return String(whole);
  if (Math.abs(fraction - 0.5) < 1e-9) return whole === 0 ? "½" : `${whole}½`;
  return String(value);
};

const humanizeToken = (value: string): string =>
  value.replaceAll("_", " ").replaceAll(":", ": ").replace(/\s+/g, " ").trim();

/** The last path segment of a URL, decoded and capped, for compact lists. */
export const urlTail = (url?: string | null): string => {
  const raw = presentString(url);
  if (!raw) return "?";
  const path = raw.split(/[?#]/)[0];
  const segment = path.split("/").filter(Boolean).pop() ?? raw;
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // A malformed escape is shown as published.
  }
  return decoded.length > 80 ? `${decoded.slice(0, 77)}...` : decoded;
};

/** Plain words for the closed gap-code set (BLD contract §2.1). */
export const COVERAGE_GAP_LABELS: Record<string, string> = {
  candidates_unread: "Plan images were found but not read",
  levels_text_exceed_plan: "Listing describes more floors than the plan shows",
  stairs_without_target: "Plan draws stairs to a floor it does not show",
  bedrooms_listing_exceed_plan: "Listing has more bedrooms than the plan shows",
  area_plan_short:
    "Plan room areas add up to less than the listing's living area",
  upper_floor_text_without_level:
    "Listing mentions a floor the plan does not show",
  photo_cues_without_level:
    "Unassigned photos show upper-floor cues the plan has no level for",
};

/** A gap code in plain words; an unknown code is humanised, not dropped. */
export const humanizeCoverageGap = (code?: string | null): string => {
  const key = presentString(code);
  if (!key) return "Unknown gap";
  return COVERAGE_GAP_LABELS[key] ?? titleCase(humanizeToken(key));
};

export type CoverageGapView = {
  code: string;
  label: string;
  /** The detector's hard flag; null when the carrier sent a bare code. */
  hard: boolean | null;
  evidence: string[];
};

/**
 * Gaps from any carrier: bare codes (building profile) or `{code, hard,
 * evidence}` objects (floor plan, room map). Deduped by code; the object form
 * wins because it carries the evidence.
 */
export const coverageGaps = (...sources: unknown[]): CoverageGapView[] => {
  const byCode = new Map<string, CoverageGapView>();
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const gap of source) {
      const code = isRecord(gap) ? presentString(gap.code) : presentString(gap);
      if (!code) continue;
      const evidence =
        isRecord(gap) && Array.isArray(gap.evidence)
          ? gap.evidence
              .map((item) => presentString(item))
              .filter((item): item is string => item !== null)
              .slice(0, 3)
          : [];
      const hard =
        isRecord(gap) && typeof gap.hard === "boolean" ? gap.hard : null;
      const known = byCode.get(code);
      if (!known) {
        byCode.set(code, {
          code,
          label: humanizeCoverageGap(code),
          hard,
          evidence,
        });
      } else {
        if (known.hard === null && hard !== null) known.hard = hard;
        if (known.evidence.length === 0 && evidence.length > 0)
          known.evidence = evidence;
      }
    }
  }
  return [...byCode.values()];
};

export type CoverageCandidateView = {
  key: string;
  url: string | null;
  tail: string;
  status: string;
  reason: string | null;
  className: string;
};

const CANDIDATE_STATUS_CLASS: Record<string, string> = {
  read: "border-emerald-500 text-emerald-600",
  unread: "border-amber-500 text-amber-600",
  dropped: "text-muted-foreground",
};

export type PlanCoverageView = {
  status: string | null;
  label: string;
  className: string;
  gaps: CoverageGapView[];
  candidates: CoverageCandidateView[];
  expectedPages: number | null;
  readPageIds: number[];
};

const COVERAGE_STATUS: Record<string, { label: string; className: string }> = {
  complete: {
    label: "coverage complete",
    className: "border-emerald-500 text-emerald-600",
  },
  partial: {
    label: "coverage partial",
    className: "border-amber-500 text-amber-600",
  },
  unknown: { label: "coverage unknown", className: "text-muted-foreground" },
};

/**
 * One coverage view from any number of carriers (profile, room map, floor
 * plan), first carrier first: status and candidates from the first that has
 * them, gaps merged. Null when no carrier has a BLD field, so today's
 * `{expected_pages, read_page_ids}` alone renders nothing new.
 */
export const planCoverageView = (
  ...raws: unknown[]
): PlanCoverageView | null => {
  const records = raws.filter(isRecord);
  const status =
    records.map((raw) => presentString(raw.status)).find(Boolean) ?? null;
  const gaps = coverageGaps(...records.map((raw) => raw.gaps));
  const candidateList =
    records
      .map((raw) => raw.candidates)
      .find((list) => Array.isArray(list) && list.length > 0) ?? [];
  const candidates = (Array.isArray(candidateList) ? candidateList : [])
    .filter(isRecord)
    .map((candidate, index) => {
      const candidateStatus = presentString(candidate.status) ?? "unknown";
      const reason = presentString(candidate.reason);
      return {
        key: `${index}:${presentString(candidate.url) ?? ""}`,
        url: presentString(candidate.url),
        tail: urlTail(presentString(candidate.url)),
        status: candidateStatus,
        reason: reason ? humanizeToken(reason) : null,
        className:
          CANDIDATE_STATUS_CLASS[candidateStatus] ?? "text-muted-foreground",
      };
    });
  if (!status && gaps.length === 0 && candidates.length === 0) return null;
  const withPages = records.find(
    (raw) => isNumber(raw.expected_pages) || Array.isArray(raw.read_page_ids),
  );
  const tone = (status && COVERAGE_STATUS[status]) || null;
  return {
    status,
    label: tone?.label ?? (status ? `coverage ${status}` : "coverage"),
    className: tone?.className ?? "text-muted-foreground",
    gaps,
    candidates,
    expectedPages:
      withPages && isNumber(withPages.expected_pages)
        ? withPages.expected_pages
        : null,
    readPageIds:
      withPages && Array.isArray(withPages.read_page_ids)
        ? withPages.read_page_ids.filter(isNumber)
        : [],
  };
};

const FOUND_BY_LABELS: Record<string, string> = {
  portal_array: "portal plan list",
  dom_container: "plan section on the page",
  lightbox_group: "lightbox group",
  cta: "floor plan link",
  pdf_anchor: "PDF link",
  brochure: "brochure page",
  gallery_classifier: "gallery classifier",
  extractor_exclusion: "extractor exclusion",
  upload: "upload",
};

export const humanizeFoundBy = (value?: string | null): string => {
  const key = presentString(value);
  if (!key) return "?";
  return FOUND_BY_LABELS[key] ?? humanizeToken(key);
};

export type FloorPlanSourceRow = {
  key: string;
  index: number | null;
  url: string | null;
  tail: string;
  kind: string | null;
  foundBy: string[];
  status: string | null;
  reason: string | null;
  pages: number[];
};

/** `floor_plan.sources[]` (BLD plan sets) in source order; empty before BLD. */
export const floorPlanSourceRows = (
  plan?: EchoflicksFloorPlan | null,
): FloorPlanSourceRow[] =>
  (plan && Array.isArray(plan.sources) ? plan.sources : [])
    .filter(isRecord)
    .map((source, position) => {
      const index = isNumber(source.source_index) ? source.source_index : null;
      const reason = presentString(source.reason);
      return {
        key: `${index ?? `p${position}`}:${presentString(source.url) ?? ""}`,
        index,
        url: presentString(source.url),
        tail: urlTail(presentString(source.url)),
        kind: presentString(source.kind),
        foundBy: (Array.isArray(source.found_by) ? source.found_by : [])
          .map((item) => presentString(item))
          .filter((item): item is string => item !== null)
          .map(humanizeFoundBy),
        status: presentString(source.status),
        reason: reason ? humanizeToken(reason) : null,
        pages: (Array.isArray(source.pages) ? source.pages : []).filter(
          isNumber,
        ),
      };
    })
    .sort(
      (a, b) =>
        (a.index ?? Number.MAX_SAFE_INTEGER) -
        (b.index ?? Number.MAX_SAFE_INTEGER),
    );

/**
 * The caption under a page thumbnail: the bot's own caption, else the floor
 * title the plan set printed (BLD `level_hint`), plus the drawing kind when
 * it is not a plain floor plan ("Site (site plan)").
 */
export const floorPlanPageCaption = (
  page?: NonNullable<EchoflicksFloorPlan["pages"]>[number] | null,
): string | null => {
  if (!isRecord(page)) return null;
  const caption = presentString(page.caption);
  if (caption) return caption;
  const hint = presentString(page.level_hint);
  const kind = presentString(page.plan_kind);
  const kindLabel = kind && kind !== "floor_plan" ? humanizeToken(kind) : null;
  if (hint && kindLabel) return `${hint} (${kindLabel})`;
  return hint ?? kindLabel;
};

const LEVEL_KIND_LABELS: Record<string, string> = {
  lower_ground: "lower ground",
};

export const humanizeLevelKind = (kind?: string | null): string | null => {
  const key = presentString(kind);
  if (!key) return null;
  return LEVEL_KIND_LABELS[key] ?? humanizeToken(key);
};

const SUBTYPE_LABELS: Record<string, string> = {
  semi_detached: "Semi-detached",
  ground_floor_flat: "Ground-floor flat",
  farm_land: "Farmland",
};

const LEVELS_DECLARED_LABELS: Record<string, string> = {
  one: "One level",
  one_and_one_half: "One and a half levels",
  two: "Two levels",
  three_or_more: "Three or more levels",
  bi_level: "Bi-level",
  multi_split: "Multi-split",
  tri_level: "Tri-level",
};

const EVIDENCE_SOURCE_LABELS: Record<string, string> = {
  agent_override: "agent override",
  structured_field: "portal field",
  json_ld: "JSON-LD",
  url_slug: "URL",
  title: "title",
  description: "description",
  plan: "floor plan",
  photo_cue: "photo cue",
  listing_text: "listing text",
  listing: "listing",
  text: "listing text",
};

export const humanizeEvidenceSource = (source?: string | null): string => {
  const key = presentString(source);
  if (!key) return "?";
  return EVIDENCE_SOURCE_LABELS[key] ?? humanizeToken(key);
};

const FACT_LABELS: Record<string, string> = {
  unit_floor: "unit floor",
  levels_declared: "levels declared",
  bedrooms_upper: "bedrooms upstairs",
};

const humanizeFact = (fact?: string | null): string => {
  const key = presentString(fact);
  if (!key) return "?";
  return FACT_LABELS[key] ?? humanizeToken(key);
};

const BAND_META: Record<string, { label: string; className: string }> = {
  accept: { label: "accept", className: "border-emerald-500 text-emerald-600" },
  review: {
    label: "needs review",
    className: "border-amber-500 text-amber-600",
  },
  reject: { label: "reject", className: "border-red-500 text-red-600" },
};

export type BuildingScoredFact = {
  value: string;
  label: string;
  confidencePercent: string | null;
};

export type BuildingProfileView = {
  /** "observe" (logged beside the legacy type) or "on" (authoritative). */
  authority: string | null;
  band: { value: string; label: string; className: string } | null;
  structure: BuildingScoredFact | null;
  subtype: BuildingScoredFact | null;
  label: {
    value: string | null;
    localeLabel: string | null;
    confidencePercent: string | null;
  } | null;
  levelsDeclared: string | null;
  /** "1½" plus "2 above ground, 1 below" when the producer split it. */
  storeys: {
    text: string;
    detail: string | null;
    confidencePercent: string | null;
  } | null;
  storeysTotal: string | null;
  unitFloor: { value: number; source: string | null } | null;
  entryLevel: { ordinal: number; source: string | null } | null;
  levelsExpected: string[];
  outbuildings: Array<{ key: string; kind: string; source: string | null }>;
  bedrooms: {
    value: number | null;
    source: string | null;
    upper: number | null;
  } | null;
  evidence: Array<{
    key: string;
    fact: string;
    value: string;
    source: string;
    quote: string | null;
    weight: string | null;
  }>;
  conflicts: Array<{
    key: string;
    fact: string;
    values: string[];
    sources: string[];
    note: string | null;
  }>;
  coverage: PlanCoverageView | null;
  /** Today's 13-value property type the profile maps onto, labelled. */
  propertyTypeCompat: string | null;
  lexiconVersion: string | null;
  policyDigest: string | null;
  /** Which payloads supplied the profile, first = the one that won. */
  carriers: string[];
};

const scoredFact = (
  raw: unknown,
  labels: Record<string, string> = {},
): BuildingScoredFact | null => {
  if (!isRecord(raw)) return null;
  const value = presentString(raw.value);
  if (!value) return null;
  return {
    value,
    label: labels[value] ?? titleCase(humanizeToken(value)),
    confidencePercent: formatPercent(
      isNumber(raw.confidence) ? raw.confidence : null,
    ),
  };
};

const displayValue = (value: unknown): string | null => {
  if (isNumber(value)) return formatStoreys(value) ?? String(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  const text = presentString(value);
  return text ? humanizeToken(text) : null;
};

const numberOrNull = (value: unknown): number | null =>
  isNumber(value) ? value : null;

/** Normalise one `building_profile.v1` block (full or room-map subset). */
export const buildingProfileView = (
  raw?: EchoflicksBuildingProfile | null,
  options: { carriers?: string[]; extraCoverage?: unknown[] } = {},
): BuildingProfileView | null => {
  if (!isRecord(raw)) return null;
  const labelRaw = isRecord(raw.label) ? raw.label : null;
  const labelValue = labelRaw ? presentString(labelRaw.value) : null;
  const localeLabel = labelRaw ? presentString(labelRaw.locale_label) : null;
  const storeysRaw = isRecord(raw.storeys) ? raw.storeys : null;
  const storeysText = storeysRaw ? formatStoreys(storeysRaw.value) : null;
  const above = numberOrNull(storeysRaw?.above_ground);
  const below = numberOrNull(storeysRaw?.below_ground);
  const storeysDetail = [
    above !== null ? `${above} above ground` : null,
    below !== null && below > 0 ? `${below} below` : null,
  ]
    .filter(Boolean)
    .join(", ");
  const storeysTotal = isRecord(raw.storeys_total)
    ? formatStoreys(raw.storeys_total.value)
    : null;
  const unitFloorRaw = isRecord(raw.unit_floor) ? raw.unit_floor : null;
  const entryRaw = isRecord(raw.entry_level) ? raw.entry_level : null;
  const bedroomsRaw = isRecord(raw.bedrooms) ? raw.bedrooms : null;
  const bandValue = presentString(raw.band);
  const levelsDeclaredValue = isRecord(raw.levels_declared)
    ? presentString(raw.levels_declared.value)
    : null;

  const view: BuildingProfileView = {
    authority: presentString(raw.authority),
    band: bandValue
      ? {
          value: bandValue,
          ...(BAND_META[bandValue] ?? {
            label: humanizeToken(bandValue),
            className: "text-muted-foreground",
          }),
        }
      : null,
    structure: scoredFact(raw.structure),
    subtype: scoredFact(raw.subtype, SUBTYPE_LABELS),
    label:
      labelValue || localeLabel
        ? {
            value: labelValue ? humanizeToken(labelValue) : null,
            localeLabel,
            confidencePercent: formatPercent(
              numberOrNull(labelRaw?.confidence),
            ),
          }
        : null,
    levelsDeclared: levelsDeclaredValue
      ? (LEVELS_DECLARED_LABELS[levelsDeclaredValue] ??
        titleCase(humanizeToken(levelsDeclaredValue)))
      : null,
    storeys: storeysText
      ? {
          text: storeysText,
          detail: storeysDetail || null,
          confidencePercent: formatPercent(
            numberOrNull(storeysRaw?.confidence),
          ),
        }
      : null,
    storeysTotal:
      storeysTotal && storeysTotal !== storeysText ? storeysTotal : null,
    unitFloor:
      unitFloorRaw && isNumber(unitFloorRaw.value)
        ? {
            value: unitFloorRaw.value,
            source: presentString(unitFloorRaw.source),
          }
        : null,
    entryLevel:
      entryRaw && isNumber(entryRaw.ordinal)
        ? { ordinal: entryRaw.ordinal, source: presentString(entryRaw.source) }
        : null,
    levelsExpected: (Array.isArray(raw.levels_expected)
      ? raw.levels_expected
      : []
    )
      .filter(isRecord)
      .map((level) => {
        const kind = humanizeLevelKind(presentString(level.kind));
        const ordinal = numberOrNull(level.ordinal);
        if (kind && ordinal !== null) return `${kind} (${ordinal})`;
        return kind ?? (ordinal !== null ? `level ${ordinal}` : null);
      })
      .filter((item): item is string => item !== null),
    outbuildings: (Array.isArray(raw.outbuildings) ? raw.outbuildings : [])
      .filter(isRecord)
      .map((item, index) => ({
        key: `${index}`,
        kind: displayValue(item.kind) ?? "outbuilding",
        source: presentString(item.source) ?? presentString(item.evidence),
      })),
    bedrooms:
      bedroomsRaw &&
      (isNumber(bedroomsRaw.value) || isNumber(bedroomsRaw.upper))
        ? {
            value: numberOrNull(bedroomsRaw.value),
            source: presentString(bedroomsRaw.source),
            upper: numberOrNull(bedroomsRaw.upper),
          }
        : null,
    evidence: (Array.isArray(raw.evidence) ? raw.evidence : [])
      .filter(isRecord)
      .slice(0, 40)
      .map((item, index) => {
        const llr = numberOrNull(item.llr);
        const weight = numberOrNull(item.weight);
        return {
          key: `${index}`,
          fact: humanizeFact(presentString(item.fact)),
          value: displayValue(item.value) ?? "?",
          source: humanizeEvidenceSource(presentString(item.source)),
          quote: presentString(item.quote),
          weight:
            llr !== null
              ? `LLR ${llr.toFixed(1)}`
              : weight !== null
                ? `weight ${weight.toFixed(2)}`
                : null,
        };
      }),
    conflicts: (Array.isArray(raw.conflicts) ? raw.conflicts : [])
      .filter(isRecord)
      .slice(0, 10)
      .map((item, index) => ({
        key: `${index}`,
        fact: humanizeFact(presentString(item.fact)),
        values: (Array.isArray(item.values) ? item.values : [])
          .map(displayValue)
          .filter((value): value is string => value !== null),
        sources: (Array.isArray(item.sources) ? item.sources : [])
          .map((source) => presentString(source))
          .filter((source): source is string => source !== null)
          .map(humanizeEvidenceSource),
        note: presentString(item.note),
      })),
    coverage: planCoverageView(raw.coverage, ...(options.extraCoverage ?? [])),
    propertyTypeCompat: propertyTypeLabel(
      presentString(raw.property_type_compat),
    ),
    lexiconVersion: presentString(raw.lexicon_version),
    policyDigest: presentString(raw.policy_digest),
    carriers: options.carriers ?? [],
  };
  const hasContent = Boolean(
    view.structure ||
      view.subtype ||
      view.label ||
      view.storeys ||
      view.unitFloor ||
      view.levelsDeclared ||
      view.band ||
      view.evidence.length > 0 ||
      view.conflicts.length > 0,
  );
  return hasContent ? view : null;
};

/** Plan §5.11's dedicated event; not in the contract yet, read when present. */
export const BUILDING_PROFILE_EVENT_TYPE = "property.building_profile";

/** The latest `property.building_profile` event's block, or null. */
export const buildingProfileFromEvents = (
  events: ReadonlyArray<EchoflicksSessionEvent>,
): EchoflicksBuildingProfile | null => {
  const matching = (events ?? [])
    .filter((event) => event.event_type === BUILDING_PROFILE_EVENT_TYPE)
    .sort((a, b) =>
      String(a.occurred_at ?? "").localeCompare(String(b.occurred_at ?? "")),
    );
  const latest = matching[matching.length - 1];
  if (!latest || !isRecord(latest.raw_payload)) return null;
  const raw = latest.raw_payload;
  const payload = isRecord(raw.payload) ? raw.payload : raw;
  const block = isRecord(payload.building_profile)
    ? payload.building_profile
    : payload;
  return block as EchoflicksBuildingProfile;
};

const isPresent = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  !(Array.isArray(value) && value.length === 0);

/**
 * The Echo's building profile from every carrier that has one, field by
 * field, first carrier first: a full block (`property.building_profile`
 * event, then the listing-ingest record) beats the room-map subset, which
 * still fills what the others lack. Coverage merges the profile's own with
 * the room map's and the floor plan's (those carry gap evidence).
 */
export const buildingProfileFromSources = ({
  events = [],
  ingest = null,
  roomMap = null,
}: {
  events?: ReadonlyArray<EchoflicksSessionEvent>;
  ingest?: EchoflicksListingIngest | null;
  roomMap?: RoomMapEventPayload | null;
}): BuildingProfileView | null => {
  const carriers: Array<[string, unknown]> = [
    ["building profile event", buildingProfileFromEvents(events)],
    ["listing ingest", ingest?.building_profile],
    ["floor plan", ingest?.floor_plan?.building_profile],
    ["room map", roomMap?.building_profile],
  ];
  const present = carriers.filter(
    (entry): entry is [string, Record<string, unknown>] => isRecord(entry[1]),
  );
  if (present.length === 0) return null;
  const merged: Record<string, unknown> = {};
  for (const [, block] of present) {
    for (const [key, value] of Object.entries(block)) {
      if (!isPresent(merged[key]) && isPresent(value)) merged[key] = value;
    }
  }
  return buildingProfileView(merged as EchoflicksBuildingProfile, {
    carriers: present.map(([name]) => name),
    extraCoverage: [roomMap?.coverage, ingest?.floor_plan?.coverage],
  });
};

/** The room map's own coverage view (orchestrator, BLD), or null. */
export const roomMapCoverageView = (
  payload?: RoomMapEventPayload | null,
): PlanCoverageView | null =>
  payload
    ? planCoverageView(payload.coverage, payload.building_profile?.coverage)
    : null;

export type RoomMapWarning = { key: string; label: string; detail: string[] };

const photosWord = (count: number) => `${count} photo${count === 1 ? "" : "s"}`;

/**
 * What the room map got wrong without saying so (plan §1.2): a level with 0
 * photos placed while photos stayed unassigned, collapsed into one line when
 * it is every level, and unassigned photos with upper-floor cues (the
 * orchestrator's `photo_cues_without_level` gap). Works on pre-BLD v2
 * payloads too: `levels[].photos` and `unassigned` exist since FPV-021.
 */
export const roomMapLevelWarnings = (
  payload?: RoomMapEventPayload | null,
): RoomMapWarning[] => {
  if (!payload) return [];
  const warnings: RoomMapWarning[] = [];
  const unassigned = isNumber(payload.unassigned) ? payload.unassigned : 0;
  const counted = roomMapLevelSummaries(payload).filter(
    (level) => level.photos !== null,
  );
  const empty = counted.filter((level) => level.photos === 0);
  if (unassigned > 0 && empty.length > 0) {
    const verb = unassigned === 1 ? "is" : "are";
    if (counted.length > 1 && empty.length === counted.length) {
      warnings.push({
        key: "levels-all-empty",
        label: `0 photos placed on every level (${counted.length} levels) while ${photosWord(unassigned)} ${verb} unassigned`,
        detail: [],
      });
    } else {
      for (const level of empty) {
        warnings.push({
          key: `level-empty-${level.levelId}`,
          label: `Level ${level.label}: 0 photos placed while ${photosWord(unassigned)} ${verb} unassigned`,
          detail: [],
        });
      }
    }
  }
  const cues = roomMapCoverageView(payload)?.gaps.find(
    (gap) => gap.code === "photo_cues_without_level",
  );
  if (cues) {
    warnings.push({
      key: "photo-cues",
      label:
        "Unassigned photos show upper-floor cues (stairs, sloped ceilings, roof windows) and no level on the plan holds them",
      detail: cues.evidence,
    });
  }
  return warnings;
};
